// Back from Paystack: what the widget says and offers once it has asked the
// return-page check (paystack-payment-status, #1466) about the payment. Pure:
// no React, no network, no storage, no clock (every time is passed in).
// Pinned by e2e/payment-return-logic.spec.ts; the hook that runs it is
// components/checkout/useCheckoutReturn.ts.
//
// Before this, the return could only poll our own database for the webhook's
// confirmation. A guest who cancelled on Paystack reads exactly like a paid
// guest whose webhook is late, so both waited about 20 seconds and were then
// told "We're still confirming…". Paystack can say at once:
//
//   confirmed / paid  "Payment received — we're confirming your booking…". The
//                     confirmation reader is asked until the webhook's
//                     confirmation is on file (the webhook confirms; this
//                     never does). Nothing that leads to Pay.
//   in_progress       "Your payment is still going through." with Check
//                     again; the check is asked again by itself a few times
//                     and the confirmation reader keeps watching. Nothing
//                     that leads to Pay.
//   not_paid          Paystack says it was cancelled, abandoned or failed:
//                     the checkout this tab kept reopens on Review & pay,
//                     "We haven't received your payment. Your details are
//                     saved." (the second-charge guard still asks Paystack
//                     before any new page).
//   refunded          a refund is on file (the money came and went back,
//                     e.g. paid after the hold ended). The confirmation
//                     reader says which (20261001160010): no booking, "We
//                     couldn't keep this booking, so your payment is being
//                     refunded in full." with Choose another time (a fresh
//                     cart); the booking stands, "Your booking is confirmed,
//                     and this payment is being refunded in full."; never
//                     "in full" when the refund on file is only part of the
//                     payment; Paystack's word alone, "This payment was
//                     refunded." Never "not received", never a promised
//                     confirmation, nothing that leads to Pay.
//   unknown           no answer to trust (the check failed, Paystack did not
//                     answer, a week-old payment): the wait as before, then
//                     "We're still confirming…" with Check again and Back to
//                     your checkout behind the check-first words.
//
// "We haven't received your payment" is said ONLY when Paystack said so
// (not_paid), and a "not paid" the server served from a stamp that may be
// older than the guest's return (cached) is asked again first.
//
// Once Paystack said paid (or still going through), this tab remembers it on
// the trip it kept (the hop record's `said`), so a reload whose check fails
// says the same again instead of falling back to the wait and its way back
// to Pay.

import type { PaymentState } from "./charge-guard";
import {
  RECEIVED_HINT,
  RECEIVED_LATE_HINT,
  RECEIVED_LATE_MESSAGE,
  RECEIVED_MESSAGE,
  REFUNDED_BOOKED_HINT,
  REFUNDED_HINT as REFUNDED_NO_BOOKING_HINT,
  refundMessage,
} from "./confirmation-outcome";
import {
  BACK_TO_CHECKOUT_HINT,
  CONFIRM_POLL_AT_MS,
  CONFIRMING_MESSAGE,
  NOT_RECEIVED_MESSAGE,
  RESUME_MAX_AGE_MS,
  STILL_CONFIRMING_MESSAGE,
  markReturned,
  redoMessage,
  type CheckoutDetails,
  type CheckoutResume,
  type CodeKind,
  type ReopenedFields,
  type ResumeGiven,
} from "./checkout-steps";

/**
 * The check's answer, as the widget reads it. A failed check is unknown /
 * check_failed. `cached`: served from the server's last Paystack answer
 * (under 5 seconds old, STATUS_CACHE_MS), which may predate the guest's
 * return. `details`: with a "not paid" answer to the payment's own cart
 * token, what the guest typed that the unpaid payment and its bookings hold
 * (Special requests, a stay's Country), so the reopened checkout shows them
 * again; absent from an older server.
 */
export type PaymentStatusAnswer = {
  state: PaymentState;
  reason: string | null;
  cached?: boolean;
  details?: CheckoutDetails | null;
};

/**
 * The check's `checkout` object (Special requests and a stay's Country the
 * unpaid payment holds), read strictly: plain, short text only, or null.
 */
export function readCheckoutDetails(v: unknown): CheckoutDetails | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const c = v as Record<string, unknown>;
  const text = (x: unknown, max: number) => (typeof x === "string" && x.trim() !== "" && x.length <= max ? x : null);
  const notes = text(c.notes, 2000);
  const country = text(c.country, 16);
  return notes || country ? { notes, country } : null;
}

/** The reason the widget gives a check it could not make (network, timeout, a 404). */
export const CHECK_FAILED = "check_failed";

/**
 * A check that has not answered in this long is a failed check (unknown).
 * Above the server's own wait for Paystack (VERIFY_TIMEOUT_MS 15 s a try,
 * VERIFY_BUDGET_MS 20 s in all, in supabase/functions/_shared/
 * paystack-charge-guard.ts), so a slow Paystack still answers instead of
 * reading as a failed check. The return keeps asking the confirmation
 * reader meanwhile, and a failed check never reopens Pay by itself.
 */
export const PAYMENT_STATUS_TIMEOUT_MS = 25_000;

/**
 * What the return shows (the drawer's "returning" view):
 *   confirming  asking ("Confirming your payment…")
 *   received    paid, the webhook is confirming it
 *   going       Paystack is still processing it
 *   not_paid    Paystack says not paid and the checkout cannot reopen
 *               (with a kept checkout, Review & pay reopens instead)
 *   refunded    a refund is on file for this payment
 *   superseded  paid on a page opened before the booking changed: it goes
 *               back in full, and the updated total can be paid while the
 *               booking is held (confirmation-outcome.ts: supersededWords,
 *               20261003200010)
 *   still       no answer to trust: "We're still confirming…"
 */
export type ReturnPhase = "confirming" | "received" | "going" | "not_paid" | "refunded" | "superseded" | "still";

/** The view an answer leads to. */
export function phaseForAnswer(answer: PaymentStatusAnswer): ReturnPhase {
  switch (answer.state) {
    case "confirmed":
    case "paid":
      return "received";
    case "in_progress":
      return "going";
    case "not_paid":
      // Refunded: received, then given back. Never "we haven't received it".
      return answer.reason === "refunded" ? "refunded" : "not_paid";
    default:
      return "still";
  }
}

/**
 * What the view offers. Nothing that leads to Pay (Back to your checkout,
 * Review & pay) while the money may be in: received and going never offer
 * either. `kept`: this tab kept the checkout (the resume entry). `late`: the
 * wait for the webhook's confirmation is over.
 */
export type ReturnOffer = {
  checkAgain: boolean;
  backToCheckout: boolean;
  reviewAndPay: boolean;
};

export function returnOffer(phase: ReturnPhase, { kept, late = false }: { kept: boolean; late?: boolean }): ReturnOffer {
  switch (phase) {
    case "received":
      return { checkAgain: late, backToCheckout: false, reviewAndPay: false };
    case "going":
      return { checkAgain: true, backToCheckout: false, reviewAndPay: false };
    case "not_paid":
      return { checkAgain: false, backToCheckout: false, reviewAndPay: kept };
    case "refunded":
      return { checkAgain: false, backToCheckout: false, reviewAndPay: false };
    case "still":
      return { checkAgain: true, backToCheckout: kept, reviewAndPay: false };
    default:
      return { checkAgain: false, backToCheckout: false, reviewAndPay: false };
  }
}

// ─── The words ──────────────────────────────────────────────────────────────

// The received words are the confirmation reader's own (confirmation-outcome.ts,
// shared with main's return): one wording wherever a paid, unconfirmed
// payment is shown.
export const PAYMENT_RECEIVED_MESSAGE = RECEIVED_MESSAGE;
export const PAYMENT_RECEIVED_HINT = RECEIVED_HINT;
export const PAYMENT_RECEIVED_LATE_MESSAGE = RECEIVED_LATE_MESSAGE;
/** No promise: the booking is not confirmed yet, and may never be (paid after the hold ended). */
export const PAYMENT_RECEIVED_LATE_HINT = RECEIVED_LATE_HINT;
export const PAYMENT_GOING_MESSAGE = "Your payment is still going through.";
/** What to do, and what happens if it does not go through (the checks stop after a few minutes). */
export const PAYMENT_GOING_HINT =
  "Please don't pay again while it's going through. Check again in a minute: if it went through, you'll see your confirmation; if it didn't, you can pay again.";
/** Paystack says not paid, and this tab kept no checkout to reopen (or it could not be reopened). */
export const NOT_PAID_MESSAGE = "We haven't received your payment.";
export const NOT_PAID_NOT_KEPT_HINT = "Your checkout wasn't kept in this tab. Close this and add it again to pay.";
/** A refund is on file (Paystack's word alone): the money went back. Which booking it was for is the guest's messages to tell. */
export const REFUNDED_MESSAGE = "This payment was refunded.";
/** Paid on a page opened before the booking changed (20261003200010), without the amounts. */
export const SUPERSEDED_MESSAGE =
  "Your booking changed after this payment page opened, so this payment is being refunded in full.";
export const REFUNDED_HINT =
  "It doesn't pay for a booking. If you have a booking, its confirmation is in your email or WhatsApp — please check there before booking again.";

/**
 * What the confirmation reader said about a refund (20261001160010):
 *   none    no booking: it could not be kept, the payment is being refunded
 *           (with Choose another time, on a fresh cart);
 *   booked  the booking stands, this payment is being refunded;
 *   null    only Paystack said refunded: the words above.
 * With `inFull` false (the refund on file is only part of the payment), the
 * words never say "in full".
 */
export type RefundKind = "none" | "booked";

/** The view's headline. */
export function returnMessage(
  phase: ReturnPhase,
  {
    late = false,
    refund = null,
    inFull = true,
  }: { late?: boolean; refund?: RefundKind | null; inFull?: boolean } = {},
): string {
  switch (phase) {
    case "received":
      return late ? PAYMENT_RECEIVED_LATE_MESSAGE : PAYMENT_RECEIVED_MESSAGE;
    case "going":
      return PAYMENT_GOING_MESSAGE;
    case "not_paid":
      return NOT_PAID_MESSAGE;
    case "refunded":
      if (refund === "none") return refundMessage(false, inFull);
      if (refund === "booked") return refundMessage(true, inFull);
      return REFUNDED_MESSAGE;
    case "superseded":
      // The view says it with the amounts (supersededWords); this is the
      // sentence without them.
      return SUPERSEDED_MESSAGE;
    case "still":
      return STILL_CONFIRMING_MESSAGE;
    default:
      return CONFIRMING_MESSAGE;
  }
}

/**
 * The line under the headline (null: none). `problem`: the view already says
 * why the kept checkout could not reopen (its hold ended), which is the
 * whole story for not_paid.
 */
export function returnHint(
  phase: ReturnPhase,
  {
    late = false,
    kept = false,
    problem = false,
    refund = null,
  }: { late?: boolean; kept?: boolean; problem?: boolean; refund?: RefundKind | null } = {},
): string | null {
  switch (phase) {
    case "received":
      return late ? PAYMENT_RECEIVED_LATE_HINT : PAYMENT_RECEIVED_HINT;
    case "going":
      return PAYMENT_GOING_HINT;
    case "not_paid":
      return kept || problem ? null : NOT_PAID_NOT_KEPT_HINT;
    case "refunded":
      if (refund === "none") return REFUNDED_NO_BOOKING_HINT;
      if (refund === "booked") return REFUNDED_BOOKED_HINT;
      return REFUNDED_HINT;
    case "still":
      return kept ? BACK_TO_CHECKOUT_HINT : null;
    default:
      return null;
  }
}

/**
 * The way back from a refund with no booking: Choose another time (a fresh
 * cart, back to the services). Only when the reader said there is no
 * booking; never beside a booking that stands, never on Paystack's word alone.
 */
export function offersChooseAnotherTime(phase: ReturnPhase, refund: RefundKind | null | undefined): boolean {
  return phase === "refunded" && refund === "none";
}

/**
 * The notice Review & pay reopens with when Paystack said not paid: the
 * payment has not been received (Paystack's own answer, so no "if you did
 * pay"), and what the guest must do again before paying, only what applies
 * (redoMessage: the terms tick, a code, and Special requests or a Country
 * the server's copy did not bring back).
 */
export function notPaidNotice(
  entry: { codesApplied: boolean; codeKinds?: readonly CodeKind[]; given?: ResumeGiven },
  fields: Pick<ReopenedFields, "notes" | "country"> | null = null,
): string {
  return `${NOT_RECEIVED_MESSAGE} ${redoMessage(entry, fields)}`;
}

// ─── Timing ─────────────────────────────────────────────────────────────────

/**
 * Asking the check again (ms after the previous ask), by the answer:
 *   checking  another request holds the one-per-5-seconds Paystack ask
 *             (the server's single flight): its answer is served from the
 *             stamp moments later, so ask again soon, still "Confirming…";
 *   going     Paystack is still processing it: ask again a few times, the
 *             view says so meanwhile, and Check again is always there;
 *   stale     "not paid", served from a stamp under 5 seconds old
 *             (cached): it may be Paystack's answer from BEFORE the guest
 *             paid (the charge guard stamps the page it hands back on Pay).
 *             Asked once more after the stamp's 5 seconds are over; that
 *             answer is after the guest's return, whatever it is.
 * Anything else is not asked again by itself.
 */
export type RecheckKind = "checking" | "going" | "stale";
export type RecheckTiming = Record<RecheckKind, readonly number[]>;
export const STATUS_RECHECK_MS: RecheckTiming = {
  checking: [1500, 2500, 4000],
  going: [6000, 15_000, 30_000],
  // Over the server's STATUS_CACHE_MS (5 s) after the first answer.
  stale: [5500],
};

/**
 * What Paystack said about this trip earlier (this page load or one before
 * it, on the trip record): "paid" is final (only a refund changes it),
 * "in_progress" until Paystack says otherwise.
 */
export type PaystackSaid = "paid" | "in_progress";

/** Which re-ask an answer calls for (null: none), given what Paystack said before. */
export function recheckKind(answer: PaymentStatusAnswer, said: PaystackSaid | null = null): RecheckKind | null {
  const base = phaseForAnswer(answer);
  if (base === "refunded" || base === "received" || said === "paid") return null;
  if (base === "not_paid") return answer.cached ? "stale" : null;
  if (base === "going" || said === "in_progress") return "going";
  return answer.state === "unknown" && answer.reason === "checking" ? "checking" : null;
}

/** When to ask again after this answer (the `attempt`-th re-ask of its kind, from 0), or null: not by itself. */
export function statusRecheckDelay(
  answer: PaymentStatusAnswer,
  attempt: number,
  timing: RecheckTiming = STATUS_RECHECK_MS,
  said: PaystackSaid | null = null,
): number | null {
  const kind = recheckKind(answer, said);
  return kind ? (timing[kind][attempt] ?? null) : null;
}

/**
 * One answer, and what Paystack said before it, as the view to show and
 * when to ask again (`recheckIn`: ms, or null for not by itself):
 *   refunded        always (a refund changes even "paid");
 *   received        Paystack says paid, or said so before (a failed check,
 *                   or a contradicting "not paid", never undoes it);
 *   not_paid        Paystack says so, fresh; a cached "not paid" is asked
 *                   again first ("confirming" meanwhile, or "going" if it
 *                   was going), and taken once the re-ask is spent;
 *   going           Paystack says it is still going through, or said so and
 *                   now gives no answer to trust;
 *   confirming      "checking": asked again soon; once spent, "still";
 *   still           no answer to trust.
 */
export function returnStep(
  answer: PaymentStatusAnswer,
  { said = null, attempt = 0 }: { said?: PaystackSaid | null; attempt?: number } = {},
  timing: RecheckTiming = STATUS_RECHECK_MS,
): { phase: ReturnPhase; recheckIn: number | null } {
  const base = phaseForAnswer(answer);
  if (base === "refunded") return { phase: "refunded", recheckIn: null };
  if (base === "received" || said === "paid") return { phase: "received", recheckIn: null };
  const recheckIn = statusRecheckDelay(answer, attempt, timing, said);
  const kind = recheckKind(answer, said);
  if (kind === "stale") {
    if (recheckIn === null) return { phase: "not_paid", recheckIn: null };
    return { phase: said === "in_progress" ? "going" : "confirming", recheckIn };
  }
  if (base === "not_paid") return { phase: "not_paid", recheckIn: null };
  if (kind === "going") return { phase: "going", recheckIn };
  if (kind === "checking" && recheckIn !== null) return { phase: "confirming", recheckIn };
  return { phase: "still", recheckIn: null };
}

/** What this tab remembers Paystack said, after showing `phase` (not paid and refunded forget it). */
export function saidAfter(said: PaystackSaid | null, phase: ReturnPhase): PaystackSaid | null {
  if (phase === "received") return "paid";
  if (phase === "going") return said === "paid" ? "paid" : "in_progress";
  if (phase === "not_paid" || phase === "refunded" || phase === "superseded") return null;
  return said;
}

/** The view a return opens on before anything is asked: what Paystack said before, or "Confirming…". */
export function initialPhase(said: PaystackSaid | null): ReturnPhase {
  return said === "paid" ? "received" : said === "in_progress" ? "going" : "confirming";
}

/**
 * The reader's wait with no answer to trust, `elapsedMs` after the return
 * began (the check may have taken most of it): the asks still to come, from
 * now, and always one now. Never the whole ~20 seconds again after a slow
 * check.
 */
export function remainingSchedule(schedule: readonly number[], elapsedMs: number): number[] {
  const rest = schedule.filter((at) => at > elapsedMs).map((at) => at - Math.max(0, elapsedMs));
  return [0, ...rest.filter((at) => at > 0)];
}

/**
 * The confirmation reader after "Payment received": the return's usual
 * asks (about 20 seconds), then every `everyMs` up to `forMs` (3 minutes).
 * Then the view says where the confirmation will arrive, with Check again.
 */
export function paidSchedule(
  base: readonly number[] = CONFIRM_POLL_AT_MS,
  { everyMs = 5000, forMs = 180_000 }: { everyMs?: number; forMs?: number } = {},
): number[] {
  const out = [...base];
  let at = out[out.length - 1] ?? 0;
  while (at + everyMs <= forMs) {
    at += everyMs;
    out.push(at);
  }
  return out;
}
export const PAID_POLL_AT_MS: readonly number[] = paidSchedule();

// ─── The trip to Paystack this tab kept ─────────────────────────────────────

/**
 * sessionStorage['vt-paystack-hop:<tenant slug>']: the payment this tab last
 * sent the guest to Paystack with, and the cart token that proves the tab
 * owns it (the check answers only its owner). Kept for every checkout that
 * leaves for Paystack, drawer or full page, including the carts the resume
 * entry cannot keep (a spa treatment, add-ons). The payment's reference and
 * the cart token only: never an answer, the booker's contact or a code.
 */
export function hopKey(tenantSlug: string): string {
  return `vt-paystack-hop:${tenantSlug}`;
}

export const HOP_VERSION = 1;

export type PaystackHopRecord = {
  v: typeof HOP_VERSION;
  reference: string;
  cartToken: string;
  startedAt: number;
  /** Paystack has since sent the browser back to the page with this reference. */
  returned: boolean;
  /**
   * What Paystack said about it since (a payment state, never an answer,
   * contact or code): a reload keeps "Payment received" / "still going
   * through" instead of falling back to the wait when its check fails.
   * It only ever holds Pay back.
   */
  said: PaystackSaid | null;
};

const shortText = (v: unknown): v is string => typeof v === "string" && v.length > 0 && v.length <= 200;

export function buildHop(reference: string, cartToken: string, now: number): PaystackHopRecord | null {
  if (!shortText(reference) || !shortText(cartToken) || cartToken.length < 8) return null;
  return { v: HOP_VERSION, reference, cartToken, startedAt: now, returned: false, said: null };
}

/** A stored record, checked strictly, or null (absent, unreadable, too old, or not this shape). */
export function parseHop(raw: string | null | undefined, now: number): PaystackHopRecord | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const h = v as Record<string, unknown>;
  if (h.v !== HOP_VERSION || !shortText(h.reference) || !shortText(h.cartToken) || h.cartToken.length < 8) return null;
  if (typeof h.startedAt !== "number" || !Number.isFinite(h.startedAt)) return null;
  if (now - h.startedAt > RESUME_MAX_AGE_MS || h.startedAt - now > 60_000) return null;
  // Only these fields come back, whatever else an edited entry carries.
  return {
    v: HOP_VERSION,
    reference: h.reference,
    cartToken: h.cartToken,
    startedAt: h.startedAt,
    returned: h.returned === true,
    said: h.said === "paid" || h.said === "in_progress" ? h.said : null,
  };
}

/** The record after Paystack sent the browser back with this reference (another reference: unchanged). */
export function markHopReturned(hop: PaystackHopRecord, reference: string): PaystackHopRecord {
  return hop.reference === reference ? { ...hop, returned: true } : hop;
}

/** The record after Paystack said this about its payment (another reference: unchanged). */
export function markHopSaid(hop: PaystackHopRecord, reference: string, said: PaystackSaid | null): PaystackHopRecord {
  return hop.reference === reference ? { ...hop, said } : hop;
}

/** What Paystack said before about this payment, as the trip record keeps it (null: nothing, or another trip). */
export function hopSaid(hop: Pick<PaystackHopRecord, "reference" | "said"> | null, reference: string): PaystackSaid | null {
  return hop && hop.reference === reference ? hop.said : null;
}

/**
 * The resume entry after Paystack sent the browser back with `reference`:
 * marked returned only when it was this entry's own trip. Any other
 * reference (another cart's payment, an old callback address) leaves it
 * alone, so the entry never vouches its cart token for a payment it did not
 * make (proofFor) and its own trip is still asked about on the next load.
 */
export function markEntryReturned(entry: CheckoutResume, reference: string, now: number): CheckoutResume {
  return entry.payment?.reference === reference ? markReturned(entry, reference, now) : entry;
}

/**
 * The cart token that proves this tab owns the payment: the trip it kept
 * with this reference, or the resume entry whose last trip it was. Null:
 * the tab kept nothing for it (another tab's payment, a gift card), and the
 * return waits as before without asking the check.
 */
export function proofFor(
  reference: string,
  hop: Pick<PaystackHopRecord, "reference" | "cartToken"> | null,
  entry: { cartToken: string; payment: { reference: string } | null } | null,
): string | null {
  if (hop && hop.reference === reference) return hop.cartToken;
  if (entry?.payment && entry.payment.reference === reference) return entry.cartToken;
  return null;
}
